import type {DefinitionOrReference} from '@github-ui/code-nav'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import type {CSSProperties} from 'react'

import styles from './HighlightedOverlay.module.css'

export function HighlightedOverlay({
  symbols,
  focusedSymbol,
  lineNumber,
  style,
  isNotUsingWhitespace,
  className,
}: {
  symbols: DefinitionOrReference[]
  focusedSymbol?: DefinitionOrReference
  lineNumber: number
  style?: CSSProperties
  isNotUsingWhitespace?: boolean
  className?: string
}) {
  let startIndex = 0
  const text = symbols.length > 0 ? symbols[0]!.bodyText : ''
  const key = `overlay-${lineNumber}-${style ? 'blob' : 'panel'}-${text}`
  const wrapOption = useCodeViewOptions().codeWrappingOption
  const leadingWhitespace = symbols.length > 0 && !isNotUsingWhitespace ? (symbols[0]!.leadingWhitespace ?? 0) : 0

  return (
    <span
      key={key}
      className={clsx(
        styles.overlayWrapper,
        wrapOption.enabled ? styles.overlayWrapperWrap : styles.overlayWrapperNoWrap,
        className,
      )}
      style={style}
    >
      {symbols.map(symbol => {
        const isFocused = symbol === focusedSymbol
        const line = (
          <span key={`symbol-${symbol.ident.start.line}-${symbol.ident.start.column + leadingWhitespace}`}>
            <span className={styles.hiddenText}>
              {text.substring(startIndex, symbol.ident.start.column + leadingWhitespace)}
            </span>
            <span
              className={clsx(
                styles.highlightBackground,
                isFocused ? styles.focusedHighlight : styles.unfocusedHighlight,
              )}
            >
              <span
                className={!isFocused ? styles.hiddenText : undefined}
                id={matchElementId(symbol.lineNumber, symbol.ident.start.column + leadingWhitespace)}
              >
                {text.substring(
                  symbol.ident.start.column + leadingWhitespace,
                  symbol.ident.end.column + leadingWhitespace,
                )}
              </span>
            </span>
          </span>
        )

        // eslint-disable-next-line react-hooks/immutability
        startIndex = symbol.ident.end.column + leadingWhitespace

        return line
      })}
      {
        // Return the rest of the line to wrap properly
        <span className={styles.hiddenText}>{text.substring(startIndex)}</span>
      }
    </span>
  )
}

export function matchElementId(line: number, column: number) {
  return `match-${line}-${column}`
}
